import { createContext, FC, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  deleteUsersInRole,
  getUsersByOrgId,
  getUsersInRole,
  postUsersInRole,
} from "../../api/broker";
import { iDataUser } from "../../interfaces/SuperAdmin/Pages/iDataUser";
import { iDataUserRoles } from "../../interfaces/SuperAdmin/Pages/iDataUserRoles";
import { useSuperAdminSingleRol } from "./SingleRoleContext";

interface UserRoleContextProps {
  isAddModalOpen: boolean;
  isDeleteModalOpen: boolean;
  isAdding: boolean;
  isDeleting: boolean;
  isLoadingUserRoles: boolean;
  toDeleteUsers: string[];
  toAddUsers: string[];
  usersByRol: iDataUserRoles[];
  allUsers: iDataUser[];
  isLoadingAllUsers: boolean;
  missingUsers: iDataUser[];
  toggleAddModal: (isOpen: boolean) => void;
  toggleDeleteModal: (isOpen: boolean) => void;
  setToDeleteUsers: React.Dispatch<React.SetStateAction<string[]>>;
  setToAddUsers: React.Dispatch<React.SetStateAction<string[]>>;
  submitSelections: (body: {
    id_rol: string;
    id_usuarios: string[];
  }) => Promise<void>;
  deleteSelections: (body: { ids: string[] }) => Promise<void>;
}

const UserRoleContext = createContext<UserRoleContextProps>(
  {} as UserRoleContextProps
);

interface Props {
  children: React.ReactNode;
  isPermissionsTab: 0 | 1;
}

const UserRoleProvider: FC<Props> = ({ children, isPermissionsTab }) => {
  const { idRol } = useParams();
  const {
    role: { id_organizacion },
  } = useSuperAdminSingleRol();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [isLoadingUserRoles, setIsLoadingUserRoles] = useState(false);
  const [isLoadingAllUsers, setIsLoadingAllUsers] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [usersByRol, setUsersByRol] = useState<iDataUserRoles[]>([]);
  const [allUsers, setAllUsers] = useState<iDataUser[]>([]);
  const [missingUsers, setMissingUsers] = useState<iDataUser[]>([]);

  const [toDeleteUsers, setToDeleteUsers] = useState<string[]>([]);
  const [toAddUsers, setToAddUsers] = useState<string[]>([]);

  // Extraer los usuarios del rol
  const fetchUsersByRol = async (id: string | undefined) => {
    if (id) {
      setIsLoadingUserRoles(true);
      try {
        const res = await getUsersInRole(id);
        console.log(res);
        if (res.data.statusCode === 200) {
          setUsersByRol(res.data.body);
        } else {
          toast.error("Error al obtener los usuarios del rol");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener los usuarios del rol");
      } finally {
        setIsLoadingUserRoles(false);
      }
    }
  };

  // Hacemos el fetch cuando la ventana sea la de usuarios
  useEffect(() => {
    if (isPermissionsTab === 1) {
      fetchUsersByRol(idRol);
    }
  }, [isPermissionsTab]);

  // Extraer los usuarios de esa organizacion
  const fetchAllUsers = async () => {
    if (id_organizacion) {
      setIsLoadingAllUsers(true);
      try {
        const res = await getUsersByOrgId(id_organizacion);
        console.log(res);
        if (res.data.statusCode === 200) {
          setAllUsers(res.data.body);
        } else {
          toast.error("Error al obtener los usuarios de la organización");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener los usuarios de la organización");
      } finally {
        setIsLoadingAllUsers(false);
      }
    }
  };

  //En caso de abrir el modal de agregar usuarios, hacemos fetch
  useEffect(() => {
    if (isAddModalOpen) {
      fetchAllUsers();
    }
  }, [isAddModalOpen]);

  useEffect(() => {
    // Hacemos un filtro de todos los usuarios. Evaluamos si alguno de los usuarios del rol ya incluye ese usuario.
    //De esta forma dejamos solo los usuarios que no han sido elegidos
    const newArray = allUsers.filter(
      (user) =>
        !usersByRol.some((userRole) => userRole.id_usuario === user.id_usuario)
    );
    setMissingUsers(newArray);
  }, [usersByRol, allUsers]);

  // Agregar usuarios a un rol
  const submitSelections = async (body: {
    id_rol: string;
    id_usuarios: string[];
  }) => {
    setIsAdding(true);
    try {
      const res = await postUsersInRole(body);
      console.log(res);
      if (res.data.statusCode === 201) {
        setUsersByRol((prev) => {
          return [...prev, ...res.data.body];
        });
        toggleAddModal(false);
        toast.success("Usuarios agregados al rol exitosamente");
      } else {
        toast.error("Error al agregar los usuarios al rol");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al agregar los usuarios al rol");
    } finally {
      setIsAdding(false);
    }
  };

  // Funcion para abrir y cerrar el modal de borrar
  const toggleDeleteModal = (isOpen: boolean) => {
    setIsDeleteModalOpen(isOpen);
  };

  // Funcion para abrir y cerrar el modal de agregar
  const toggleAddModal = (isOpen: boolean) => {
    setIsAddModalOpen(isOpen);
  };

  // Borrar usuarios del rol
  const deleteSelections = async (body: { ids: string[] }) => {
    setIsDeleting(true);
    try {
      const res = await deleteUsersInRole(body);
      console.log(res);
      if (res.data.statusCode === 200) {
        setToDeleteUsers([]);
        setUsersByRol((prev) =>
          prev.filter((item) => !body.ids.includes(item.id_usuario_rol))
        );
        toggleDeleteModal(false);
        toast.success("Usuarios eliminados del rol exitosamente");
      } else {
        toast.error("Error al eliminar los usuarios del rol");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al eliminar los usuarios del rol");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <UserRoleContext.Provider
      value={{
        isAddModalOpen,
        isDeleteModalOpen,
        isAdding,
        isDeleting,
        isLoadingUserRoles,
        toDeleteUsers,
        usersByRol,
        allUsers,
        toAddUsers,
        isLoadingAllUsers,
        missingUsers,
        toggleAddModal,
        toggleDeleteModal,
        setToDeleteUsers,
        setToAddUsers,
        submitSelections,
        deleteSelections,
      }}
    >
      {children}
    </UserRoleContext.Provider>
  );
};

const useSuperAdminUserRole = () => useContext(UserRoleContext);

export { UserRoleProvider, useSuperAdminUserRole };
